// ── Client Invoice Notices ──────────────────────────────────────────────────
//
// The page that emails BORROWERS. Everything else in ACE that sends mail sends
// it to staff, and the difference is the whole design of this screen.
//
// An internal notice sent wrongly is noise somebody forgives. A borrower notice
// sent wrongly — wrong month, twice, to a former client — is a phone call, and
// it cannot be recalled. So the page is arranged so that the wrong send is
// harder to perform than the right one:
//
//   the list comes first    you see every name and address before any button
//                           to send is enabled
//   a proof copy first      one real message, real template, real period, to
//                           your own address. Reading the month in an actual
//                           letter is the strongest check there is
//   or the month is typed   for when you have not proofed it. A confirm dialog
//                           does not catch the wrong month, because clicking
//                           Yes is the same gesture whichever month is loaded.
//                           EITHER satisfies the send — requiring both made the
//                           careful path slower than the careless one, which is
//                           how a control stops being used
//   sent is visible         anyone already told about this period is shown and
//                           struck through, so the count on the button is the
//                           number that will actually go
//
// The gaps panel is the other half of review. It lists funded active accounts
// whose statement IS published but who have nobody flagged for Invoice
// Delivery — the borrower who quietly never gets told, which no amount of
// staring at the recipient list would reveal.

import { org } from "../../../shared/org";
import { useState, useEffect, useCallback, useMemo } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

// The client-facing sender. NOT m365send: that row's domain fence is what
// stops an internal notice reaching a borrower, and widening it here would
// remove that protection from every internal notification at the same time.
const CLIENT_PROVIDER = "m365client";

const hint = { fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: 1.5 };
const inp  = { padding: "7px 10px", fontSize: 13, borderRadius: 6,
               border: "1px solid var(--color-border-secondary, #ccd3d8)",
               background: "var(--color-background-primary, #fff)",
               color: "var(--color-text-primary, #1a1a1a)", boxSizing: "border-box" };

const chip = (bg, fg) => ({ display: "inline-block", padding: "1px 7px", borderRadius: 4,
                            fontSize: 11, fontWeight: 600, background: bg, color: fg,
                            whiteSpace: "nowrap" });
const OK   = chip("#dff3ec", "#0F6E56");
const WARN = chip("#fdeccd", "#7a4f00");
const BAD  = chip("#f7d9cf", "#993C1D");

const banner = (tone) => ({
  margin: "0 0 14px", padding: "10px 13px", borderRadius: 7, fontSize: 13, lineHeight: 1.55,
  background: tone === "bad" ? "#f7d9cf" : tone === "warn" ? "#fdeccd" : "#dff3ec",
  color:      tone === "bad" ? "#993C1D" : tone === "warn" ? "#7a4f00" : "#0F6E56",
});

export const ClientInvoiceNoticesPage = () => {
  const { api, role, go } = useApp();

  const [options, setOptions] = useState(null);
  const [period, setPeriod]   = useState("");
  const [stype, setStype]     = useState("");
  const [data, setData]       = useState(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [msg, setMsg]         = useState(null);

  const [excluded, setExcluded] = useState(() => new Set());
  const [confirm, setConfirm]   = useState("");
  const [override, setOverride] = useState(false);
  const [reason, setReason]     = useState("");
  const [testTo, setTestTo]     = useState("");
  const [tested, setTested]     = useState(false);
  const [progress, setProgress] = useState(null);
  const [sentMail, setSentMail] = useState(null);   // the letter a past run sent

  // Read, not edited. The mailbox is configured under Administrative →
  // Integrations → Client Mail; this page only needs to know whether it EXISTS,
  // because the Send button must not arm before there is something to send
  // through. Keeping the read here rather than trusting the page to have been
  // visited is what makes that guard real.
  const [cfg, setCfg] = useState(null);

  // The months that actually have something published. A borrower cannot be
  // told to go and look at a document that was never made visible, so those
  // months are not offered in the first place.
  const loadOptions = useCallback(async () => {
    setErr(null);
    try {
      const d = await api.get("previewInvoiceNotices");
      setOptions(d.options || []);
    } catch (e) { setErr(e.message); }
    try {
      setCfg(await api.get("getIntegrations", { provider: CLIENT_PROVIDER }));
    } catch { setCfg(null); }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const loadPreview = useCallback(async (p, t) => {
    if (!p || !t) { setData(null); return; }
    setBusy(true); setErr(null); setMsg(null);
    // Anything carried over from the last selection is a hazard, not a
    // convenience: a tick list or a typed month that belonged to September
    // must not survive into an October send.
    setExcluded(new Set()); setConfirm(""); setOverride(false);
    setReason(""); setTested(false); setProgress(null);
    try {
      setData(await api.get("previewInvoiceNotices", { period: p, statementtype: t }));
    } catch (e) { setErr(e.message); setData(null); }
    setBusy(false);
  }, [api]);

  const pick = (value) => {
    const [p, t] = value.split("|");
    setPeriod(p || ""); setStype(t || "");
    loadPreview(p, t);
  };

  const toggle = (contactid) => setExcluded(s => {
    const n = new Set(s);
    n.has(contactid) ? n.delete(contactid) : n.add(contactid);
    return n;
  });

  const willSend = useMemo(() => {
    if (!data) return [];
    return data.recipients.filter(r => !r.alreadySent && !excluded.has(r.contactid));
  }, [data, excluded]);

  const sendTest = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("sendInvoiceNotices", {
        period, statementtype: stype, test: true, testTo: testTo.trim(),
        sampleName: (data && data.recipients[0] && data.recipients[0].contactname) || undefined,
        sampleCompany: (data && data.recipients[0] && data.recipients[0].companyname) || undefined,
      });
      if (r.tested) {
        setTested(true);
        setMsg({ tone: "ok", text:
          `Proof copy sent to ${r.to} from ${r.from}. `
          + (r.savedToSentItems
              ? `A copy was requested in ${r.from}'s Sent Items — if it is not there, Exchange declined to file it rather than ACE failing to ask.`
              : `NO copy was requested, so Sent Items will stay empty: the mailbox's "Keep a copy" setting is off, or the API host is still running code from before it was saved.`)
          + ` Check the letter before sending the batch.` });
      }
      else { setMsg({ tone: "bad", text: `Test failed: ${r.error}` }); }
    } catch (e) { setMsg({ tone: "bad", text: e.message }); }
    setBusy(false);
  };

  const viewSent = async (runid) => {
    setBusy(true); setMsg(null);
    try { setSentMail(await api.get("previewInvoiceNotices", { runid })); }
    catch (e) { setMsg({ tone: "bad", text: e.message }); }
    setBusy(false);
  };

  // Called repeatedly until done. The endpoint works to a time budget and
  // hands back a runid; passing it in resumes rather than starting again.
  const send = async () => {
    setBusy(true); setMsg(null); setProgress({ sent: 0, remaining: willSend.length });
    let runid = null;
    try {
      for (let pass = 0; pass < 50; pass++) {
        const r = await api.post("sendInvoiceNotices", {
          period, statementtype: stype,
          confirm, runid,
          excludeContactIds: [...excluded],
          override, overrideReason: reason,
        });
        runid = r.runid;
        setProgress({ sent: r.totals.sent, failed: r.totals.failed, remaining: r.remaining });
        if (r.fatal) { setMsg({ tone: "bad", text: `Stopped: ${r.fatal}` }); break; }
        if (r.done) {
          setMsg({ tone: r.totals.failed ? "warn" : "ok",
            text: `${r.totals.sent} sent`
                + (r.totals.failed ? `, ${r.totals.failed} failed` : "")
                + (r.totals.excluded ? `, ${r.totals.excluded} excluded` : "")
                + (r.totals.skipped ? `, ${r.totals.skipped} skipped as duplicates` : "")
                + "." });
          break;
        }
      }
      await loadPreview(period, stype);
    } catch (e) { setMsg({ tone: "bad", text: e.message }); }
    setBusy(false);
  };

  const senderReady = !!(cfg && cfg.isactive && cfg.config && cfg.config.sender);

  if (loading) return <Spinner />;
  if (err && !options) return <ErrorMsg msg={err} onRetry={loadOptions} />;

  const phrase   = data ? data.confirmPhrase : "";
  // Either satisfies the server. `tested` is this session's proof; data.proofed
  // is one recorded earlier — so reloading the page, or coming back tomorrow,
  // does not ask you to prove it again.
  const proofed  = tested || (data && data.proofed);
  const typedConfirmOk = !!phrase && confirm.trim().toLowerCase() === phrase.toLowerCase();
  // Either is enough. Both are shown, so it is never a mystery which one armed
  // the button — or, when it is disabled, what is missing.
  const typedOk  = proofed || typedConfirmOk;
  const needsReason = override && !reason.trim();
  const canSend  = role.canEdit && data && willSend.length > 0 && typedOk && !busy && senderReady
                   && (!data.counts.alreadySent || override) && !needsReason;

  return <>
    <SectionHeader title="Client Invoice Notices" actions={[
      <Btn key="r" onClick={() => loadPreview(period, stype)} disabled={busy || !period}>Reload</Btn>,
    ]} />

    <div style={banner("warn")}>
      <strong>This sends email to borrowers.</strong> Every other notification in ACE goes to
      staff. Nothing here can be recalled once it leaves, so the list below is the review — read
      it, not the count.
    </div>

    {msg && <div style={banner(msg.tone)}>{msg.text}</div>}
    {err && data && <div style={banner("bad")}>{err}</div>}

    {!senderReady && (
      <div style={banner("bad")}>
        <strong>The client mailbox is not set up, so nothing can be sent.</strong> Borrower mail
        cannot go through the internal notifications sender — its allowed-domains list is what
        stops an internal notice reaching a client, and widening it would remove that protection
        everywhere. Set up the separate mailbox under{" "}
        <a href="#" onClick={e => { e.preventDefault(); go("integration-clientmail"); }}
           style={{ color: "#993C1D", fontWeight: 600 }}>
          Administrative → Integrations → Client Mail</a>.
      </div>
    )}

    {senderReady && cfg.config.allowdomains && cfg.config.allowdomains.length > 0 && (
      <div style={banner("warn")}>
        <strong>The client mailbox is fenced to {cfg.config.allowdomains.join(", ")}.</strong>{" "}
        Anything outside those domains will be refused rather than sent, which is right for a
        rehearsal and wrong for a live batch. Clear it under Integrations → Client Mail when you
        are ready to send for real.
      </div>
    )}

    {/* ── What to send ──────────────────────────────────────────────────── */}
    <Card style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, fontWeight: 500 }}>Period and document</label>
        <select style={{ ...inp, minWidth: 340 }} disabled={busy}
                value={period && stype ? `${period}|${stype}` : ""}
                onChange={e => pick(e.target.value)}>
          <option value="">Choose…</option>
          {(options || []).map(o => (
            <option key={`${o.period}|${o.statementtype}`} value={`${o.period}|${o.statementtype}`}>
              {o.period} — {o.statementtype} ({o.accounts} account{o.accounts === 1 ? "" : "s"} published)
            </option>
          ))}
        </select>
      </div>
      <div style={hint}>
        Only months with <strong>published</strong> statements appear. The notice tells the
        borrower the document is in their portal, so a month where nothing is published is not
        something that can honestly be announced.
      </div>
    </Card>

    {busy && !data && <Spinner />}

    {data && <>
      {/* ── Counts ──────────────────────────────────────────────────────── */}
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 600 }}>{willSend.length}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>will be emailed</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 600 }}>{data.counts.accounts}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>borrowers</div>
          </div>
          {data.counts.alreadySent > 0 && (
            <div>
              <div style={{ fontSize: 26, fontWeight: 600, color: "#993C1D" }}>{data.counts.alreadySent}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>already told</div>
            </div>
          )}
          {excluded.size > 0 && (
            <div>
              <div style={{ fontSize: 26, fontWeight: 600, color: "#7a4f00" }}>{excluded.size}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>excluded by you</div>
            </div>
          )}
        </div>

        {data.duplicates.length > 0 && (
          <div style={{ ...banner("warn"), margin: "10px 0 0" }}>
            <strong>{data.duplicates.length} address{data.duplicates.length === 1 ? " appears" : "es appear"} on
            more than one borrower.</strong> Only the first is written to; the rest are recorded as
            duplicates rather than sent, so nobody receives two copies.
            <div style={{ marginTop: 5, fontFamily: "var(--font-mono)", fontSize: 11 }}>
              {data.duplicates.join(", ")}
            </div>
          </div>
        )}
      </Card>

      {/* ── Gaps ────────────────────────────────────────────────────────── */}
      {data.gaps.length > 0 && (
        <Card style={{ padding: 18, marginBottom: 16 }}>
          <SectionHeader title={`${data.gaps.length} borrower${data.gaps.length === 1 ? "" : "s"} will not be told`} />
          <div style={{ ...hint, marginBottom: 10 }}>
            Funded and active, statement published for this period, and nobody to send it to.
            Usually a contact record that was never ticked rather than a decision — worth fixing
            before the batch, because these borrowers simply never hear.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead><tr><Th>Borrower</Th><Th>Status</Th><Th>Contacts</Th><Th>Flagged</Th><Th>Why</Th></tr></thead>
              <tbody>
                {data.gaps.map(g => (
                  <tr key={g.accountid} style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                    <Td>{g.companyname || g.accountid}</Td>
                    <Td>{g.accountstatus}</Td>
                    <Td>{g.contacts}</Td>
                    <Td>{g.flagged}</Td>
                    <Td style={{ color: "var(--color-text-secondary)" }}>{g.reason}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── The list ────────────────────────────────────────────────────── */}
      <Card style={{ padding: 18, marginBottom: 16 }}>
        <SectionHeader title="Recipients" actions={[
          // Only ever touches rows that are actually selectable. An
          // already-sent row is disabled, and sweeping it into the excluded set
          // would inflate the "excluded by you" count with people nobody
          // decided anything about.
          <Btn key="none" onClick={() => setExcluded(new Set(
                 data.recipients.filter(r => !r.alreadySent).map(r => r.contactid)))}
               disabled={busy || willSend.length === 0}>
            Unselect all
          </Btn>,
          <Btn key="all" onClick={() => setExcluded(new Set())}
               disabled={busy || excluded.size === 0}>
            Select all
          </Btn>,
        ]} />
        <div style={{ ...hint, marginBottom: 10 }}>
          Untick anyone who should not receive this run. That is recorded against the run and does
          not change their Invoice Delivery flag — it is a decision about today, not about them.
          <br />
          <strong>Unselect all</strong> then ticking a handful is the safer way round when you mean
          to send to only a few: the count on the Send button always tells you how many will go, so
          starting from none makes an accidental send impossible rather than merely unlikely.
        </div>
        <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
            <thead><tr>
              <Th></Th><Th>Borrower</Th><Th>Status</Th><Th>Contact</Th><Th>Email</Th><Th></Th>
            </tr></thead>
            <tbody>
              {data.recipients.map(r => {
                const off = r.alreadySent || excluded.has(r.contactid);
                return (
                  <tr key={r.contactid}
                      style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)",
                               opacity: off ? 0.5 : 1 }}>
                    <Td>
                      <input type="checkbox" disabled={busy || r.alreadySent}
                             checked={!off} onChange={() => toggle(r.contactid)} />
                    </Td>
                    <Td>{r.companyname || r.accountid}</Td>
                    <Td><span style={r.accountstatus === "Non Accrual" ? WARN : OK}>{r.accountstatus}</span></Td>
                    <Td>{r.contactname || "—"}</Td>
                    <Td style={{ textDecoration: r.alreadySent ? "line-through" : "none" }}>{r.email}</Td>
                    <Td>{r.alreadySent
                      ? <span style={BAD}>already sent</span>
                      : excluded.has(r.contactid) ? <span style={WARN}>excluded</span> : null}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Send ────────────────────────────────────────────────────────── */}
      <Card style={{ padding: 18 }}>
        <SectionHeader title="Send" />

        {/* ── Two ways to confirm the month, either one enough ────────────
            Typing it is the quick one. Reading it in a real letter is the
            stronger one. Requiring BOTH made the careful path slower than the
            careless one, which is how a control stops being used — so either
            arms the button, and the state of both is always on screen. */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>
            Confirm the period {typedConfirmOk && <span style={OK}>matches</span>}
            {!typedConfirmOk && confirm.trim() !== "" && <span style={BAD}>does not match</span>}
          </div>
          <input style={{ ...inp, minWidth: 280 }} value={confirm} disabled={busy}
                 placeholder={phrase} onChange={e => setConfirm(e.target.value)} />
          <div style={hint}>
            Type <code>{phrase}</code> to confirm what is going out. Optional — a proof copy below
            confirms it too, and reading the month in a real letter is the better check. Whichever
            you use is verified on the server, because a control that exists only on this page is a
            suggestion.
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>
            Send yourself a proof copy {proofed && <span style={OK}>done</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input style={{ ...inp, minWidth: 280 }} value={testTo} disabled={busy}
                   placeholder={"your.name@" + org("emailDomain")}
                   onChange={e => setTestTo(e.target.value)} />
            <Btn onClick={sendTest} disabled={busy || !testTo.trim()}>Send proof copy</Btn>
          </div>
          <div style={hint}>
            The real template, the real period, through the real client mailbox — so it proves the
            wording and the plumbing at once. Read the month in it before going further.
            {proofed && !tested && data.lastProofAt
              ? ` A proof of ${data.periodLabel} already went out on `
                + `${String(data.lastProofAt).replace("T", " ").slice(0, 16)}.`
              : ""}
          </div>
        </div>

        {data.counts.alreadySent > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={override} disabled={busy}
                     onChange={e => setOverride(e.target.checked)} />
              <span>
                <strong>{data.counts.alreadySent}</strong> of these addresses have already been told
                about {data.periodLabel}. Send anyway.
              </span>
            </label>
            {override && (
              <>
                <input style={{ ...inp, width: "100%", marginTop: 8 }} value={reason} disabled={busy}
                       placeholder="Why is this going out twice?"
                       onChange={e => setReason(e.target.value)} />
                <div style={hint}>
                  Recorded against the run. Note the already-sent rows stay struck through and are
                  not re-sent — the override is for the rest of the list.
                </div>
              </>
            )}
          </div>
        )}

        {!typedOk && willSend.length > 0 && (
          <div style={{ ...banner("warn"), margin: "0 0 12px" }}>
            Type the period above, or send yourself a proof copy. Either unlocks the send.
          </div>
        )}

        {progress && (
          <div style={{ ...banner("ok"), margin: "0 0 12px" }}>
            {progress.sent} sent{progress.failed ? `, ${progress.failed} failed` : ""}
            {progress.remaining > 0 ? ` · ${progress.remaining} to go…` : " · finished"}
          </div>
        )}

        {/* The period is IN THE BUTTON because the typed box may not be on
            screen. Whatever else is true, the last thing read before clicking
            should name the month going out. */}
        <Btn variant="primary" onClick={send} disabled={!canSend}>
          {busy ? "Sending…"
                : `Send ${data.periodLabel} to ${willSend.length} recipient${willSend.length === 1 ? "" : "s"}`}
        </Btn>
        {!proofed && willSend.length > 0 && (
          <span style={{ ...hint, marginLeft: 10, display: "inline" }}>
            A proof copy is the cheapest way to catch a wrong month — and it saves typing this one.
          </span>
        )}

        {data.runs.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Earlier runs for {data.periodLabel}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
              <thead><tr>
                <Th>When</Th><Th>By</Th><Th>Sent</Th><Th>Failed</Th><Th>Excluded</Th><Th>Status</Th><Th>Note</Th><Th></Th>
              </tr></thead>
              <tbody>
                {data.runs.map(r => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                    <Td style={{ color: "var(--color-text-secondary)" }}>
                      {String(r.startedat || "").replace("T", " ").slice(0, 16)}</Td>
                    <Td>{r.startedby || "—"}</Td>
                    <Td>{r.sent}</Td>
                    <Td>{r.failed}</Td>
                    <Td>{r.excluded}</Td>
                    <Td>{r.status}</Td>
                    <Td style={{ whiteSpace: "normal", maxWidth: 320,
                                 color: "var(--color-text-secondary)" }}>
                      {r.isoverride ? `Override: ${r.overridereason || "no reason given"}` : (r.lasterror || "—")}
                    </Td>
                    <Td>
                      {r.hasbody
                        ? <Btn onClick={() => viewSent(r.id)} disabled={busy}>View email</Btn>
                        : <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}
                                title="Runs from before the message was kept have nothing stored.">
                            not kept
                          </span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sentMail && (
          <div style={{ marginTop: 16, borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)",
                        paddingTop: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                What that run sent
              </span>
              <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
                {sentMail.run.periodLabel} · {sentMail.run.sent} recipient
                {sentMail.run.sent === 1 ? "" : "s"} · {sentMail.run.startedby}
              </span>
              <button type="button" onClick={() => setSentMail(null)}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                               fontSize: 11.5, color: B.cyan, textDecoration: "underline" }}>
                close
              </button>
            </div>
            {sentMail.missing ? (
              <div style={{ ...banner("warn"), margin: 0 }}>
                This run predates the message being kept, so there is no copy. What it said can be
                reconstructed from the wording history on Integrations → Client Mail, but this is
                not that — it is a gap, and saying so is better than showing something that only
                looks like the original.
              </div>
            ) : <>
              <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                <span style={{ color: "var(--color-text-secondary)" }}>Subject: </span>
                <strong>{sentMail.run.subject}</strong>
              </div>
              {/* The greeting names whichever borrower happened to be first in
                  that run. Everything else was identical for everyone on it. */}
              <div style={{ ...hint, marginTop: 0, marginBottom: 8 }}>
                One copy per run. Every message differed only in the greeting, which here is the
                first recipient's.
              </div>
              <iframe title="sent-email" srcDoc={sentMail.run.samplehtml} sandbox=""
                      style={{ width: "100%", height: 460, borderRadius: 7, background: "#fff",
                               border: "1px solid var(--color-border-tertiary, #e6e9eb)" }} />
            </>}
          </div>
        )}
      </Card>
    </>}
  </>;
};
